// ChartNew.jsx
// Rewritten chart component — smooth, TradingView-style
//
// Key improvements over Chart.jsx:
// 1. Both candlestick + line series created at init — switch via visible, no data refetch
// 2. All 5 EMA series created at init — toggle via visible, no recreate
// 3. Single _ohlcvCache — unified data model for all chart types
// 4. Markers fetched once for full date range — filtered from memory on scroll
// 5. Generic markers system — any report passes fetchFn, no chart changes needed
// 6. Prepend/append preserves viewport — no jump on scroll
// 7. EMA source change redraws from cached indicator data
// 8. Auto-play support
// 9. Crosshair + range sync across grid charts

import { dumpEngineCSV } from './trendlineEngineOverlay';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  createChart,
  CandlestickSeries,
  LineSeries,
} from 'lightweight-charts';
import useStore from '../store';
import {
  fetchCandles,
  fetchCandlesByRange,
  fetchCandlesAfter,
  fetchIndicators,
  fetchIndicatorsByRange,
  fetchTradesForChart,
  fetchLiveCandle,
  fetchVPatternsForChart,
} from '../api';
import {
  buildMarkersFromTrades,
  applyMarkersToSeries,
  removeMarkersFromSeries,
} from './tradeMarkers';
// ── TREND LINE ──
import {
  initTrendLineSeries,
  drawTrendLine,
  applyTrendLineVisibility,
} from './trendLineOverlay';
// ── TREND LINE ENGINE (new) ──
import {
  initTrendlineEngineSeries,
  drawTrendlineEngine,
  applyTrendlineEngineVisibility,
} from './trendlineEngineOverlay';


// ============================================================
// CONSTANTS
// ============================================================
const INITIAL_LIMIT  = 300;
const LOAD_THRESHOLD = 50;
const INTRADAY_TF    = ['2min', '5min', '15min', '1H'];

// ── Zoom defaults (bars visible) — one step out from old 60/120 ──
const DEFAULT_BARS_INTRADAY = 120;
const DEFAULT_BARS_HTF      = 240;
const ZOOM_STORAGE_KEY      = 'chart_zoom_by_tf';

const defaultBars = (tf) =>
  INTRADAY_TF.includes(tf) ? DEFAULT_BARS_INTRADAY : DEFAULT_BARS_HTF;

const rightPadFor = (tf) =>
  INTRADAY_TF.includes(tf) ? 5 : 15;

// Saved value is the full logical SPAN (bars + right pad) so that
// saving and restoring are symmetric — no creep across reloads.
function getSavedSpan(tf) {
  try {
    const map = JSON.parse(localStorage.getItem(ZOOM_STORAGE_KEY) || '{}');
    const v   = parseFloat(map[tf]);
    return Number.isFinite(v) && v >= 10 && v <= 5000 ? v : null;
  } catch (_) { return null; }
}

function saveSpan(tf, span) {
  try {
    const map = JSON.parse(localStorage.getItem(ZOOM_STORAGE_KEY) || '{}');
    map[tf] = Math.round(span * 10) / 10;
    localStorage.setItem(ZOOM_STORAGE_KEY, JSON.stringify(map));
  } catch (_) { /* private mode etc. — persistence just won't stick */ }
}
const EMA_PERIODS    = ['ema_5', 'ema_9', 'ema_20', 'ema_50', 'ema_200'];
const IST_OFFSET_MS  = 5.5 * 60 * 60 * 1000;

// ── Generic marker fetch function registry ─────────────────
// Add new report types here — ChartNew.jsx never needs to change for new reports
const MARKER_FETCH_FNS = {
  vpatterns: (symbol, tf, filters) =>
    fetchVPatternsForChart(symbol, tf, filters),
  // future reports:
  // sr_levels:  (symbol, tf, filters) => fetchSRLevels(symbol, tf, filters),
  // crossovers: (symbol, tf, filters) => fetchCrossoversForChart(symbol, tf, filters),
};

// ============================================================
// CHART REGISTRY — module-level, outside React
// Allows charts to directly call each other for crosshair + range sync
// ============================================================
const chartRegistry = {};

// Which chart the mouse is currently over.
// ONLY this chart reacts to its own range changes (lazy load,
// zoom save) and broadcasts sync to the others. This structurally
// breaks all echo loops: receivers never re-broadcast.
let activeChartIndex = null;

function registerChart(index, entry) {
  chartRegistry[index] = {
    ...entry,
    isSyncing:          false,
    isCrosshairSyncing: false,
  };
}

function unregisterChart(index) {
  delete chartRegistry[index];
}

function syncCrosshairToOthers(sourceIndex, time) {
  Object.entries(chartRegistry).forEach(([idx, entry]) => {
    if (parseInt(idx) === sourceIndex) return;
    if (entry.isCrosshairSyncing) return;
    const series = entry.getMainSeries();
    if (!series) return;
    if (time === null) {
      entry.chart.clearCrosshairPosition();
      return;
    }
    const ts = entry.chart.timeScale();
    const x  = ts.timeToCoordinate(time);
    if (x === null) {
      // Time outside visible range — scroll to it first
      const cache = entry.getCache();
      const range = ts.getVisibleLogicalRange();
      if (cache && cache.length > 0 && range) {
        let lo = 0, hi = cache.length - 1, nearIdx = 0;
        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          if (cache[mid].time <= time) { nearIdx = mid; lo = mid + 1; }
          else hi = mid - 1;
        }
        const half = (range.to - range.from) / 2;
        entry.isSyncing = true;
        ts.setVisibleLogicalRange({ from: nearIdx - half, to: nearIdx + half });
        entry.isSyncing = false;
      }
    }
    entry.isCrosshairSyncing = true;
    entry.chart.setCrosshairPosition(Infinity, time, series);
    entry.isCrosshairSyncing = false;
  });
}

function syncRangeToOthers(sourceIndex, logicalRange) {
  if (!logicalRange) return;
  const sourceEntry = chartRegistry[sourceIndex];
  if (!sourceEntry) return;
  const sourceTS   = sourceEntry.chart.timeScale();
  const centerL    = (logicalRange.from + logicalRange.to) / 2;
  const centerX    = sourceTS.logicalToCoordinate(centerL);
  const centerTime = sourceTS.coordinateToTime(centerX);
  if (!centerTime) return;

  Object.entries(chartRegistry).forEach(([idx, entry]) => {
    if (parseInt(idx) === sourceIndex) return;
    const ts = entry.chart.timeScale();
    const x  = ts.timeToCoordinate(centerTime);
    if (x !== null) return; // already visible — crosshair sync handles it

    const cache = entry.getCache();
    const range = ts.getVisibleLogicalRange();
    if (!cache || cache.length === 0 || !range) return;
    let lo = 0, hi = cache.length - 1, nearIdx = 0;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (cache[mid].time <= centerTime) { nearIdx = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    const half = (range.to - range.from) / 2;
    const from = nearIdx - half;
    const to   = nearIdx + half;

    // Epsilon guard — skip near-no-op sets (they still fire events)
    if (Math.abs(from - range.from) < 0.5 &&
        Math.abs(to   - range.to)   < 0.5) return;

    entry.isSyncing = true;
    ts.setVisibleLogicalRange({ from, to });
    entry.isSyncing = false;  // range events fire synchronously inside set
  });
}

// ============================================================
// HELPERS
// ============================================================
function toChartTime(bucket) {
  const utcMs  = new Date(bucket).getTime();
  const istMs  = utcMs + IST_OFFSET_MS;
  const istWall = new Date(istMs);
  return Math.floor(istWall.getTime() / 1000);
}

function buildVPatternMarkers(vPatterns) {
  if (!vPatterns || vPatterns.length === 0) return [];
  return vPatterns.map(vp => {
    const t         = toChartTime(vp.pattern_time);
    const direction = vp.direction;

    // Entry markers — report builder
    if (direction === 'buy') {
      return {
        time:     t,
        position: 'belowBar',
        shape:    'arrowUp',
        color:    '#26a69a',
        text:     'B',
      };
    }
    if (direction === 'sell') {
      return {
        time:     t,
        position: 'aboveBar',
        shape:    'arrowDown',
        color:    '#ef5350',
        text:     'S',
      };
    }

    // Exit marker — condition exit
    if (direction === 'exit') {
      return {
        time:     t,
        position: 'aboveBar',
        shape:    'arrowDown',
        color:    '#ff9800',
        text:     'X',
      };
    }

    // SL hit marker
    if (direction === 'sl') {
      return {
        time:     t,
        position: 'aboveBar',
        shape:    'square',
        color:    '#d32f2f',
        text:     'SL',
      };
    }

    // Default — existing V pattern markers (up/down from vpattern report)
    const isBuy = direction === 'up';
    return {
      time:     t,
      position: isBuy ? 'belowBar' : 'aboveBar',
      shape:    isBuy ? 'arrowUp'  : 'arrowDown',
      color:    isBuy ? '#26a69a'  : '#ef5350',
      text:     'V',
      id:       `vp_${vp.pattern_time}_${vp.ema}`,
    };
  }).filter(Boolean).sort((a, b) => a.time - b.time);
}


// ============================================================
// TRADE MARKERS from markersBySymbol rows (Trades review mode)
// Each trade row → up to 2 markers (entry + exit).
// Timestamp basis MUST match candle times: candles use
// toChartTime(bucket) = IST-wall-clock-as-unix. Trade entry_date/
// entry_time are already IST wall-clock, so we parse them as if UTC
// (append 'Z') to land on the same basis — then the caller snaps
// each to its containing candle bucket.
// ============================================================
function tradeRowToIstUnix(dateStr, timeStr, fallbackTime) {
  const d = String(dateStr).slice(0, 10);
  const t = (timeStr && String(timeStr).slice(0, 8)) || fallbackTime;
  // Parse as UTC so the number equals the IST wall-clock reading in unix
  // seconds — the same basis toChartTime(bucket) produces for candles.
  const ms = new Date(`${d}T${t}Z`).getTime();
  return Math.floor(ms / 1000);
}

// Pure builder: given trade rows and the ascending candle-time array,
// returns markers snapped to the containing bucket. Isolated from other
// marker paths — only the markerKind:'trade' branch calls it.
function buildTradeMarkersFromRows(rows, cacheTimes, cache) {
  if (!rows || rows.length === 0 || !cacheTimes || cacheTimes.length === 0) return [];

  const firstT = cacheTimes[0];
  const lastT  = cacheTimes[cacheTimes.length - 1];

  const snap = (t) => {
    let lo = 0, hi = cacheTimes.length - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (cacheTimes[mid] <= t) { ans = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    return ans;
  };

  const out = [];
  rows.forEach((tr) => {
    // Entry marker
    const eT = tradeRowToIstUnix(tr.entry_date, tr.entry_time, '09:15:00');
    const ei = (eT >= firstT && eT <= lastT) ? snap(eT) : -1;
    if (ei >= 0) {
      const isBuy = (tr.entry_action || '').toLowerCase().trim() === 'buy';
      out.push({
        time:     cache[ei].time,
        position: 'belowBar',
        shape:    'arrowUp',
        color:    isBuy ? '#26a69a' : '#ef5350',
        text:     isBuy ? 'B' : 'S',
      });
    }
    // Exit marker (only if the trade has an exit)
    if (tr.exit_date) {
      const xT = tradeRowToIstUnix(tr.exit_date, tr.exit_time, '15:30:00');
      const xi = (xT >= firstT && xT <= lastT) ? snap(xT) : -1;
      if (xi >= 0) {
        const exitIsBuy = (tr.exit_action || '').toLowerCase().trim() === 'buy';
        out.push({
          time:     cache[xi].time,
          position: 'aboveBar',
          shape:    'arrowDown',
          color:    exitIsBuy ? '#26a69a' : '#ef5350',
          text:     'X',
        });
      }
    }
  });
  return out.sort((a, b) => a.time - b.time);
}

// Distinct glyphs per overlay layer (EMA-strategy report)
function buildLayeredMarkers(layer, rows) {
  if (!rows || rows.length === 0) return [];
  if (layer === 'trades') {
    return rows.map(r => {
      const t = toChartTime(r.pattern_time);
      if (r.direction === 'buy')
        return { time: t, position: 'belowBar', shape: 'arrowUp',   color: '#26a69a', text: 'B' };
      if (r.direction === 'sell')
        return { time: t, position: 'aboveBar', shape: 'arrowDown', color: '#ef5350', text: 'S' };
      return   { time: t, position: 'aboveBar', shape: 'arrowDown', color: '#ff9800', text: 'X' }; // exit
    });
  }
  if (layer === 'ema5') {
    return rows.map(r => {
      const t = toChartTime(r.pattern_time);
      return r.kind === 'high'
        ? { time: t, position: 'aboveBar', shape: 'circle', color: '#9c27b0' }
        : { time: t, position: 'belowBar', shape: 'circle', color: '#9c27b0' };
    });
  }
  // price pivots
  return rows.map(r => {
    const t = toChartTime(r.pattern_time);
    return r.kind === 'high'
      ? { time: t, position: 'aboveBar', shape: 'square', color: '#607d8b' }
      : { time: t, position: 'belowBar', shape: 'square', color: '#607d8b' };
  });
}



// ============================================================
// INDICATORS MODAL
// ============================================================
const EMA_SOURCE_OPTIONS = ['close', 'high', 'low'];

function IndicatorsModal({
  settings,
  onToggleEMA, onSetEMASource,
  onSetEMAStyle, onSetLineStyle,
  onClose,
  isGrid, gridIndex, toggleTrendLine, toggleTrendLineMarkers,  // ── TREND LINE ──
  toggleTrendlineEngine,onSetTrendlineN,                                       // ── TREND LINE ENGINE (new) ──
}) {
  const LINE_SOURCES = ['close', 'high', 'low'];
  const WIDTHS = [1, 2, 3];

  return (
    <div style={modal.overlay} onClick={onClose}>
      <div style={modal.box} onClick={e => e.stopPropagation()}>
        <div style={modal.header}>
          <span style={modal.title}>Indicators</span>
          <button style={modal.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* EMA Periods */}
        <div style={modal.section}>
          <div style={modal.sectionLabel}>EMA Periods</div>
          {EMA_PERIODS.map(ema => {
            const style = settings.emaStyles[ema];
            return (
              <div key={ema} style={modal.styleRow}>
                <label style={modal.checkRowInline}>
                  <input
                    type="checkbox"
                    checked={!!settings.visibleEMAs[ema]}
                    onChange={() => onToggleEMA(ema)}
                    style={{ marginRight: 6, accentColor: style.color }}
                  />
                  <span style={{ ...modal.colorDot, background: style.color }} />
                  <span style={modal.checkLabel}>{ema.replace('ema_', 'EMA ')}</span>
                </label>
                <input
                  type="color"
                  value={style.color}
                  onChange={e => onSetEMAStyle(ema, 'color', e.target.value)}
                  style={modal.colorPicker}
                />
                <div style={modal.widthBtns}>
                  {WIDTHS.map(w => (
                    <button key={w}
                      onClick={() => onSetEMAStyle(ema, 'width', w)}
                      style={{ ...modal.widthBtn, ...(style.width === w ? modal.widthBtnActive : {}) }}>
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* EMA Source */}
        <div style={modal.section}>
          <div style={modal.sectionLabel}>Apply EMA on</div>
          <div style={modal.radioRow}>
            {EMA_SOURCE_OPTIONS.map(src => (
              <label key={src} style={modal.radioLabel}>
                <input
                  type="radio"
                  name="emaSource"
                  value={src}
                  checked={settings.emaSource === src}
                  onChange={() => onSetEMASource(src)}
                  style={{ marginRight: 4 }}
                />
                {src.charAt(0).toUpperCase() + src.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Line Style — only when line chart */}
        {settings.chartType === 'line' && (
          <div style={modal.section}>
            <div style={modal.sectionLabel}>Line Style</div>
            {LINE_SOURCES.map(src => {
              const style = settings.lineStyles[src];
              return (
                <div key={src} style={modal.styleRow}>
                  <span style={modal.checkLabel}>
                    {src.charAt(0).toUpperCase() + src.slice(1)}
                  </span>
                  <input
                    type="color"
                    value={style.color}
                    onChange={e => onSetLineStyle(src, 'color', e.target.value)}
                    style={modal.colorPicker}
                  />
                  <div style={modal.widthBtns}>
                    {WIDTHS.map(w => (
                      <button key={w}
                        onClick={() => onSetLineStyle(src, 'width', w)}
                        style={{ ...modal.widthBtn, ...(style.width === w ? modal.widthBtnActive : {}) }}>
                        {w}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* ── TREND LINE — single chart only ── */}
        {!isGrid && (
          <div style={modal.section}>
            <div style={modal.sectionLabel}>Trend Line</div>
            <label style={modal.checkRowInline}>
              <input
                type="checkbox"
                checked={settings.showTrendlineEngine || false}
                onChange={() => toggleTrendlineEngine(gridIndex)}
              />
              <span style={{ marginLeft: '8px', fontSize: '13px' }}>
                Trend Line — Engine (new)
              </span>
              <span style={{ marginLeft: '10px', display: 'inline-block',
                width: '24px', height: '3px', background: '#2962FF',
                verticalAlign: 'middle' }} />
            </label>
            <label style={modal.checkRowInline}>
              <span style={{ fontSize: '13px' }}>Pivot N</span>
              <input
                type="number" min="1" max="20"
                value={settings.trendlineN ?? 3}
                onChange={e => onSetTrendlineN(Math.max(1, parseInt(e.target.value, 10) || 3))}
                style={{ marginLeft: '8px', width: '48px' }}
              />
            </label>
            <label style={modal.checkRowInline}>
              <input
                type="checkbox"
                checked={settings.showTrendLine || false}
                onChange={() => toggleTrendLine(gridIndex)}
              />
              <span style={{ marginLeft: '8px', fontSize: '13px' }}>
                Show Trend Line
              </span>
              <span style={{
                marginLeft: '10px',
                display: 'inline-block',
                width: '24px',
                height: '3px',
                background: '#FF6B35',
                verticalAlign: 'middle',
              }} />
            </label>
            <label style={modal.checkRowInline}>
              <input
                type="checkbox"
                disabled={!settings.showTrendLine}
                checked={settings.showTrendLineMarkers || false}
                onChange={() => toggleTrendLineMarkers(gridIndex)}
              />
              <span style={{
                marginLeft: '8px', fontSize: '13px',
                color: settings.showTrendLine ? '#191919' : '#aaa',
              }}>
                Show H/L Pivot Markers
              </span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN CHART COMPONENT
// ============================================================
function ChartNew({ gridIndex = 0, isGrid = false }) {
  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const resizeObserverRef = useRef(null);
  const isInitialLoadRef = useRef(false);
  const zoomSaveTimerRef = useRef(null);

  // ── Series refs — created once at init, never destroyed ──
  // Price series
  const candleSeriesRef   = useRef(null);
  const lineSeriesRef     = useRef({});   // { close, high, low }
  // EMA series — one per period, always present
  const emaSeriesRef      = useRef({});   // { ema_5, ema_9, ... }
  const mainSeriesRef     = useRef(null);
  const currentSymbolRef  = useRef('');

  // ── Data caches ──
  const ohlcvCacheRef     = useRef([]);   // unified [{time,open,high,low,close}]
  const indicatorCacheRef = useRef([]);   // raw indicator rows from DB
  const markersAllRef     = useRef([]);   // all markers for full date range
  // ── Layered overlays (EMA strategy report) ──
  const layeredActiveRef  = useRef(false);
  const layersRef         = useRef({ trades: [], ema5: [], price: [] });
  const togglesRef        = useRef({ showTrades: true, showEma5Piv: true, showPricePiv: true });
  const tradesRef         = useRef([]);
  const tradesRawRef   = useRef([]);   // raw trades for current symbol

  // ── Load state ──
  const loadIdRef             = useRef(0);
  const isLoadingMoreRef      = useRef(false);
  const isLoadingMoreRightRef = useRef(false);
  const hasMoreLeftRef        = useRef(true);
  const hasMoreRightRef       = useRef(true);
  const oldestBucketRef       = useRef(null);
  const newestBucketRef       = useRef(null);
  const lastSizeRef = useRef({ w: 0, h: 0 });
  // ── TREND LINE ──
  const trendLineSeriesRef = useRef(null);
  // ── TREND LINE ENGINE (new) ──
  const trendlineEngineSeriesRef = useRef(null);
  const trendlineEngineStatesRef = useRef(null);   // time → {state, sl_level, action}

  // ── Store ──
  const {
    selectedSymbol,
    chartSettings,
    setChartSetting,
    toggleChartEMA,
    toggleTrendLine,          // ── TREND LINE ──
    toggleTrendLineMarkers,   // ── TREND LINE markers (R4b) ──
    toggleTrendlineEngine,    // ── TREND LINE ENGINE (new) ──
    showMarkers,
    jumpTrigger,
    markersConfig,
    tradeReviewMode,
  } = useStore();

  
  const [showPrice, setShowPrice] = useState(true);   // review toggle: hide price, keep EMAs
  const [showTrades,   setShowTrades]   = useState(true);
  const [showEma5Piv,  setShowEma5Piv]  = useState(true);
  const [showPricePiv, setShowPricePiv] = useState(true);
  const layeredMode = !!(markersConfig && markersConfig.layers);
  const settings = chartSettings[gridIndex] || chartSettings[0];
  const {
    timeframe, chartType, lineSources,
    emaSource, visibleEMAs,
    showTrendLine,          // ── TREND LINE ──
    showTrendLineMarkers,   // ── TREND LINE markers (R4b) ──
    showTrendlineEngine,    // ── TREND LINE ENGINE (new) ──
    trendlineN,             // ── TREND LINE ENGINE (new) — pivot lookback ──
  } = settings;

  const [loading,   setLoading]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error,     setError]     = useState(null);
  const [legend,    setLegend]    = useState(null);
  const [jumpDate,  setJumpDate]  = useState('');

  // ── Shortcuts ──
  const setTimeframe  = tf  => setChartSetting(gridIndex, 'timeframe', tf);
  const setChartType  = ct  => setChartSetting(gridIndex, 'chartType', ct);
  const setEMASource  = src => setChartSetting(gridIndex, 'emaSource', src);
  const toggleEMA     = ema => toggleChartEMA(gridIndex, ema);

  const setEMAStyle = (ema, prop, val) => {
    const current = useStore.getState().chartSettings[gridIndex].emaStyles;
    setChartSetting(gridIndex, 'emaStyles', {
      ...current,
      [ema]: { ...current[ema], [prop]: val },
    });
  };

  const setLineStyle = (src, prop, val) => {
    const current = useStore.getState().chartSettings[gridIndex].lineStyles;
    setChartSetting(gridIndex, 'lineStyles', {
      ...current,
      [src]: { ...current[src], [prop]: val },
    });
  };

  const toggleLineSource = src => {
    const cur = lineSources.includes(src)
      ? lineSources.filter(s => s !== src)
      : [...lineSources, src];
    if (cur.length === 0) return;
    setChartSetting(gridIndex, 'lineSources', cur);
  };

  // ── TREND LINE ──
  useEffect(() => {
    if (!chartRef.current || ohlcvCacheRef.current.length === 0) return;
    applyTrendLineVisibility(
      trendLineSeriesRef,
      ohlcvCacheRef.current,
      showTrendLine,
      3,
      showTrendLineMarkers,
    );
  }, [showTrendLine, showTrendLineMarkers]);

  // ── TREND LINE ENGINE (new) ──
  useEffect(() => {
    if (!chartRef.current || ohlcvCacheRef.current.length === 0) return;
    applyTrendlineEngineVisibility(
      trendlineEngineSeriesRef,
      ohlcvCacheRef.current,
      showTrendlineEngine,
      trendlineN ?? 3,
      trendlineEngineStatesRef,
    );
  }, [showTrendlineEngine, trendlineN]);
  
  
  // ============================================================
  // INIT CHART — once on mount
  // Creates all series upfront. Never destroyed or recreated.
  // ============================================================
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const isIntraday = INTRADAY_TF.includes(
      useStore.getState().chartSettings[gridIndex].timeframe
    );

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#FFFFFF' },
        textColor:  '#191919',
      },
      grid: {
        vertLines: { color: '#E0E3EB' },
        horzLines: { color: '#E0E3EB' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#D1D4DC' },
      timeScale: {
        borderColor:    '#D1D4DC',
        timeVisible:    isIntraday,
        secondsVisible: false,
      },
      width:  chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    chartRef.current = chart;

    // ── Create candlestick series ──
    candleSeriesRef.current = chart.addSeries(CandlestickSeries, {
      upColor:       '#26a69a',
      downColor:     '#ef5350',
      borderVisible: false,
      wickUpColor:   '#26a69a',
      wickDownColor: '#ef5350',
      visible:       true,
    });

    mainSeriesRef.current = candleSeriesRef.current;

    // ── Create line series — one per source (close/high/low) ──
    const defaultLineStyles = useStore.getState().chartSettings[gridIndex].lineStyles;
    ['close', 'high', 'low'].forEach(src => {
      const style = defaultLineStyles[src];
      lineSeriesRef.current[src] = chart.addSeries(LineSeries, {
        color:            style.color,
        lineWidth:        style.width,
        priceLineVisible: false,
        visible:          false,  // hidden until line mode active
      });
    });

    // ── Create EMA series — one per period, all hidden initially ──
    const defaultEMAStyles = useStore.getState().chartSettings[gridIndex].emaStyles;
    EMA_PERIODS.forEach(ema => {
      const style = defaultEMAStyles[ema];
      emaSeriesRef.current[ema] = chart.addSeries(LineSeries, {
        color:            style.color,
        lineWidth:        style.width,
        priceLineVisible: false,
        visible:          false,
      });
    });

    // ── TREND LINE — create series once, hidden initially ──
    trendLineSeriesRef.current = initTrendLineSeries(chart, LineSeries);
    // ── TREND LINE ENGINE (new) — create series once, hidden ──
    trendlineEngineSeriesRef.current = initTrendlineEngineSeries(chart, LineSeries);

    // ── Resize observer ──
    resizeObserverRef.current = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) return;
        const el = chartContainerRef.current;
        if (!el || !chartRef.current) return;
        const w = el.clientWidth;
        const h = el.clientHeight;
        if (w === lastSizeRef.current.w && h === lastSizeRef.current.h) return;
        lastSizeRef.current = { w, h };
        chartRef.current.applyOptions({ width: w, height: h });
      });
    });
    resizeObserverRef.current.observe(chartContainerRef.current);

    // ── Scroll handler — lazy load ──
    chart.timeScale().subscribeVisibleLogicalRangeChange(range => {
      if (!range) return;
      if (isInitialLoadRef.current) return;   // suppress during initial load

      const entry = chartRegistry[gridIndex];
      if (entry?.isSyncing) return;            // change came from sync — ignore
      if (activeChartIndex !== gridIndex) return; // only the mouse-over chart reacts

      if (range.from < LOAD_THRESHOLD) loadMoreLeft();
      const cacheLen = ohlcvCacheRef.current.length;
      if (cacheLen > 0 && range.to > cacheLen - LOAD_THRESHOLD) loadMoreRight();

      // Range sync to other grid charts
      if (entry) syncRangeToOthers(gridIndex, range);

      // ── Save zoom span per timeframe — debounced ──
      clearTimeout(zoomSaveTimerRef.current);
      zoomSaveTimerRef.current = setTimeout(() => {
        const tfNow = useStore.getState().chartSettings[gridIndex].timeframe;
        saveSpan(tfNow, range.to - range.from);
      }, 500);
    });

    // ── Crosshair sync + legend ──
    chart.subscribeCrosshairMove(param => {
      if (!param || !param.time) {
        setLegend(null);
        return;
      }

      // Build legend
      const cs = candleSeriesRef.current;
      let ohlc = null;
      if (cs) {
        const d = param.seriesData.get(cs);
        if (d) ohlc = { o: d.open, h: d.high, l: d.low, c: d.close };
      }
      let lineVal = null;
      ['close', 'high', 'low'].forEach(src => {
        const s = lineSeriesRef.current[src];
        if (s) {
          const d = param.seriesData.get(s);
          if (d) lineVal = { ...(lineVal || {}), [src]: d.value };
        }
      });
      const emas = {};
      EMA_PERIODS.forEach(ema => {
        const s = emaSeriesRef.current[ema];
        if (s) {
          const d = param.seriesData.get(s);
          if (d) emas[ema] = d.value;
        }
      });
      // ── TREND LINE ENGINE (new) — state/SL/action at hovered candle ──
      let trend = null;
      const teMap = trendlineEngineStatesRef.current;
      if (teMap) {
        const st = teMap.get(param.time);
        if (st) trend = { state: st.state, sl: st.sl_level, action: st.action };
      }
      setLegend({ ohlc, lineVal, emas, trend });

      // Sync crosshair to other charts — only from the mouse-over chart
      const entry = chartRegistry[gridIndex];
      if (entry && !entry.isCrosshairSyncing && isGrid
          && activeChartIndex === gridIndex) {
        syncCrosshairToOthers(gridIndex, param.time);
      }
    });

    // ── Register in chart registry ──
    registerChart(gridIndex, {
      chart,
      getMainSeries: () =>
        candleSeriesRef.current?.options?.visible !== false
          ? candleSeriesRef.current
          : lineSeriesRef.current['close'],
      getCache: () => ohlcvCacheRef.current,
    });

    return () => {
      if (activeChartIndex === gridIndex) activeChartIndex = null;
      clearTimeout(zoomSaveTimerRef.current);
      resizeObserverRef.current?.disconnect();
      chart.remove();
      chartRef.current = null;
      unregisterChart(gridIndex);
    };
  }, []);

  // ============================================================
  // UPDATE TIMESCALE VISIBILITY WHEN TIMEFRAME CHANGES
  // ============================================================
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      timeScale: {
        timeVisible:    INTRADAY_TF.includes(timeframe),
        secondsVisible: false,
      },
    });
  }, [timeframe]);

  // ============================================================
  // LOAD CHART DATA — full reload
  // Triggered by symbol or timeframe change
  // Chart type change does NOT trigger this — handled separately
  // ============================================================
  const lastLoadedSymbolRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;

    const symbolChanged = lastLoadedSymbolRef.current !== selectedSymbol;
    lastLoadedSymbolRef.current = selectedSymbol;

    const reviewMode = useStore.getState().tradeReviewMode;

    // In trade-review mode, when the SYMBOL changes, the jumpTrigger effect
    // fires the correct-date jump. This effect's activeJumpDateRef still holds
    // the PREVIOUS trade's date, so jumping here would race jumpTrigger with a
    // stale date. So: on symbol change, defer to jumpTrigger and skip.
    // On timeframe change (symbol same), activeJumpDateRef is correct — re-jump.
    if (reviewMode && symbolChanged) {
      return;   // symbol switch in trades mode → jumpTrigger drives
    }

    if (activeJumpDateRef.current) {
      jumpToDate(activeJumpDateRef.current);
    } else {
      loadChartData();
    }
  }, [selectedSymbol, timeframe]);

  // ============================================================
  // CHART TYPE SWITCH — instant, no data refetch
  // Just toggle visibility on existing series
  // ============================================================
  useEffect(() => {
    if (!chartRef.current || ohlcvCacheRef.current.length === 0) return;
    applyChartTypeVisibility();
  }, [chartType, lineSources, showPrice]);

  // ============================================================
  // EMA TOGGLE — instant visibility change
  // If data not yet loaded for this EMA, fetch it
  // ============================================================
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    applyEMAVisibility();
  }, [visibleEMAs]);

  // ============================================================
  // EMA SOURCE CHANGE — redraw from cached data if possible
  // ============================================================
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    if (indicatorCacheRef.current.length > 0) {
      applyIndicatorDataToEMAs(indicatorCacheRef.current, emaSource);
    }
  }, [emaSource]);

  // ============================================================
  // EMA STYLE CHANGE — instant applyOptions
  // ============================================================
  useEffect(() => {
    if (!chartRef.current) return;
    const emaStyles = useStore.getState().chartSettings[gridIndex].emaStyles;
    EMA_PERIODS.forEach(ema => {
      const s = emaSeriesRef.current[ema];
      if (!s) return;
      const st = emaStyles[ema];
      s.applyOptions({ color: st.color, lineWidth: st.width });
    });
  }, [settings.emaStyles]);

  // ============================================================
  // LINE STYLE CHANGE — instant applyOptions
  // ============================================================
  useEffect(() => {
    if (!chartRef.current) return;
    const lineStyles = useStore.getState().chartSettings[gridIndex].lineStyles;
    ['close', 'high', 'low'].forEach(src => {
      const s = lineSeriesRef.current[src];
      if (!s) return;
      const st = lineStyles[src];
      s.applyOptions({ color: st.color, lineWidth: st.width });
    });
  }, [settings.lineStyles]);

  // ============================================================
  // MARKERS TOGGLE — show/hide without refetch
  // ============================================================
  useEffect(() => {
    if (!chartRef.current) return;
    applyMarkersFromCache();
  }, [showMarkers]);

  // Layer toggles — rebuild the combined overlay
  useEffect(() => {
    togglesRef.current = { showTrades, showEma5Piv, showPricePiv };
    if (layeredActiveRef.current) applyLayeredMarkersRef.current?.();
  }, [showTrades, showEma5Piv, showPricePiv]);

  // ============================================================
  // MARKERS CONFIG CHANGE — refetch markers for new report filters
  // ============================================================
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    // Only rebuild immediately if the cache is already populated for the
    // current symbol. If not (e.g. Load-on-Chart fired before data loaded),
    // loadChartData will rebuild markers after its fetch completes.
    if (ohlcvCacheRef.current.length > 0) {
      fetchAndApplyMarkers(selectedSymbol, timeframe);
    }
  }, [markersConfig]);

  // ============================================================
  // JUMP TO DATE
  // ============================================================
  const lastJumpIdRef = useRef(useStore.getState().jumpTrigger?.id ?? null);
  const activeJumpDateRef = useRef(null);

  useEffect(() => {
    if (!jumpTrigger) return;
    const dateStr = typeof jumpTrigger === 'string'
      ? jumpTrigger
      : jumpTrigger.date;
    const jumpId  = typeof jumpTrigger === 'object'
      ? jumpTrigger.id
      : dateStr;
    if (!dateStr) return;
    if (lastJumpIdRef.current === jumpId) return;  // stale/already handled
    lastJumpIdRef.current = jumpId;
    jumpToDate(dateStr);
  }, [jumpTrigger]);

  // ============================================================
  // LIVE CANDLE POLLING
  // ============================================================
  useEffect(() => {
    if (!selectedSymbol || !INTRADAY_TF.includes(timeframe)) return;
    const poll = async () => {
      try {
        const c = await fetchLiveCandle(selectedSymbol, timeframe);
        if (!c || !c.candle || !chartRef.current) return;
        const chartTime = toChartTime(c.candle.bucket);
        candleSeriesRef.current?.update({
          time:  chartTime,
          open:  parseFloat(c.candle.open),
          high:  parseFloat(c.candle.high),
          low:   parseFloat(c.candle.low),
          close: parseFloat(c.candle.close),
        });
        ['close', 'high', 'low'].forEach(src => {
          lineSeriesRef.current[src]?.update({
            time:  chartTime,
            value: parseFloat(c.candle[src]),
          });
        });
      } catch (_) {}
    };
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [selectedSymbol, timeframe]);

  // ============================================================
  // LOAD CHART DATA — full reload implementation
  // ============================================================
  const loadChartData = async () => {
    if (!chartRef.current || !selectedSymbol) return;

    isInitialLoadRef.current = true;   // block scroll handler
    const myLoadId = ++loadIdRef.current;
    const symbol   = selectedSymbol;
    const tf       = useStore.getState().chartSettings[gridIndex].timeframe;
    const ct       = useStore.getState().chartSettings[gridIndex].chartType;
    const src      = useStore.getState().chartSettings[gridIndex].emaSource;

    // Reset state
    oldestBucketRef.current = null;
    newestBucketRef.current = null;
    isLoadingMoreRef.current      = false;
    isLoadingMoreRightRef.current = false;
    hasMoreLeftRef.current  = true;
    hasMoreRightRef.current = true;
    EMA_PERIODS.forEach(ema => emaSeriesRef.current[ema]?.setData([]));
    //ohlcvCacheRef.current      = [];
    //indicatorCacheRef.current  = [];
    markersAllRef.current      = [];
    currentSymbolRef.current = symbol;

    //setLoading(true);
    setError(null);
    setLegend(null);

    try {
      // ── Fetch candles ──
      const candles = await fetchCandles(symbol, tf, INITIAL_LIMIT);
      if (myLoadId !== loadIdRef.current) return;
      if (!candles || candles.length === 0) {
        setError('No data');
        //setLoading(false);
        return;
      }

      // ── Build OHLCV cache ──
      const ohlcv = candles.map(c => ({
        time:  toChartTime(c.bucket),
        open:  parseFloat(c.open),
        high:  parseFloat(c.high),
        low:   parseFloat(c.low),
        close: parseFloat(c.close),
      }));
      ohlcvCacheRef.current  = ohlcv;
      oldestBucketRef.current = candles[0].bucket;
      newestBucketRef.current = candles[candles.length - 1].bucket;

      // ── Set data on ALL price series (visible or not) ──
      // This ensures instant chart type switch without refetch
      candleSeriesRef.current?.setData(ohlcv);
      ['close', 'high', 'low'].forEach(src => {
        const lineData = ohlcv.map(c => ({ time: c.time, value: c[src] }));
        lineSeriesRef.current[src]?.setData(lineData);
      });

      // ── Apply correct visibility immediately ──
      applyChartTypeVisibility();

      if (myLoadId !== loadIdRef.current) return;

      // ── Set viewport ──
      // ── Set viewport — always show latest candles ──
      // ── Set viewport — latest candles with right padding ──
      
      // ── Set viewport — latest candles with right padding ──
      const total    = ohlcv.length;
      const rightPad = rightPadFor(tf);
      const span     = getSavedSpan(tf) || (defaultBars(tf) + rightPad);
      chartRef.current?.priceScale('right').applyOptions({
        autoScale: true,
      });
      chartRef.current?.timeScale().setVisibleLogicalRange({
        from: total + rightPad - span,
        to:   total + rightPad,
      });
      // ── Fetch indicators ──
      const indicators = await fetchIndicators(symbol, tf, INITIAL_LIMIT);
      if (myLoadId !== loadIdRef.current) return;
      if (indicators && indicators.length > 0) {
        indicatorCacheRef.current = indicators;
        applyIndicatorDataToEMAs(indicators, src);
        applyEMAVisibility();
      }
        // ── TREND LINE — draw after candle data loaded ──
      const tlSet = useStore.getState().chartSettings[gridIndex];
      drawTrendLine(trendLineSeriesRef, ohlcvCacheRef.current,
                    tlSet.showTrendLine, 3, tlSet.showTrendLineMarkers);
      // ── TREND LINE ENGINE (new) ──
      drawTrendlineEngine(trendlineEngineSeriesRef, ohlcvCacheRef.current,
                          tlSet.showTrendlineEngine, tlSet.trendlineN ?? 3,
                          trendlineEngineStatesRef);

      window.dumpEngine = () => dumpEngineCSV(ohlcvCacheRef.current,
                                              tlSet.trendlineN ?? 3);
                          // ── Fetch markers ──
      // For trade-review, rebuild from the selection config now that the
      // cache is populated (fixes markers not showing until Next press).
      await fetchAndApplyMarkers(symbol, tf, myLoadId);
      if (myLoadId !== loadIdRef.current) return;

      // ── FINAL REPOSITION ──
      // Indicator / trend line / marker application above can nudge
      // the viewport. Re-assert the intended view on the next frame,
      // THEN re-enable the scroll handler.
      requestAnimationFrame(() => {
        if (myLoadId !== loadIdRef.current) return;
        isInitialLoadRef.current = false;  // re-enable scroll handler
        if (activeJumpDateRef.current) return;   // jump will set its own range — don't override
        const len = ohlcvCacheRef.current.length;
        chartRef.current?.timeScale().setVisibleLogicalRange({
          from: len + rightPad - span,
          to:   len + rightPad,
        });
      });

      applyMarkersFromCache();   // ← ADD HERE: cache is now populated, re-snap markers

      setLoading(false);
    } catch (err) {
      if (myLoadId !== loadIdRef.current) return;
      isInitialLoadRef.current = false;  // re-enable on error too
      setError('Failed to load data');
      setLoading(false);
    }
   
  };

  // ============================================================
  // APPLY CHART TYPE VISIBILITY — no data refetch
  // ============================================================
  const applyChartTypeVisibility = useCallback(() => {
    const ct  = useStore.getState().chartSettings[gridIndex].chartType;
    const ls  = useStore.getState().chartSettings[gridIndex].lineSources;
    const isCandle = ct === 'candlestick';

    candleSeriesRef.current?.applyOptions({ visible: showPrice && isCandle });
    ['close', 'high', 'low'].forEach(src => {
      lineSeriesRef.current[src]?.applyOptions({
        visible: showPrice && !isCandle && ls.includes(src),
      });
    });

    mainSeriesRef.current = isCandle
      ? candleSeriesRef.current
      : (lineSeriesRef.current[ls[0]] || lineSeriesRef.current['close']);

    applyMarkersFromCache();
  }, [gridIndex, showPrice]);

  // ============================================================
  // APPLY EMA VISIBILITY — toggle visible on existing series
  // If data missing for this EMA, fetch it
  // ============================================================
  const applyEMAVisibility = useCallback(async () => {
    if (!chartRef.current) return;
    const s   = useStore.getState().chartSettings[gridIndex];
    const ems = s.visibleEMAs;
    const src = s.emaSource;

    EMA_PERIODS.forEach(ema => {
      const series = emaSeriesRef.current[ema];
      if (!series) return;
      const shouldShow = !!ems[ema];
      // Check if data is loaded for this EMA
      const hasData = indicatorCacheRef.current.some(
        i => i[`${ema}_${src}`] != null
      );
      if (shouldShow && !hasData && indicatorCacheRef.current.length === 0) {
        // No indicator data at all — keep hidden until load completes
        series.applyOptions({ visible: false });
        return;
      }
      series.applyOptions({ visible: shouldShow && hasData });
    });
  }, [gridIndex]);

  // ============================================================
  // APPLY INDICATOR DATA TO EMA SERIES
  // Reads from indicatorCacheRef — no fetch needed
  // ============================================================
  const applyIndicatorDataToEMAs = useCallback((indicators, src) => {
    if (!chartRef.current) return;
    EMA_PERIODS.forEach(ema => {
      const series = emaSeriesRef.current[ema];
      if (!series) return;
      const colName = `${ema}_${src}`;
      const data = indicators
        .filter(i => i[colName] != null)
        .map(i => ({ time: toChartTime(i.time), value: parseFloat(i[colName]) }));
      if (data.length > 0) {
        series.setData(data);
      }
    });
  }, []);

  // ============================================================
  // FETCH AND APPLY MARKERS — fetched once, applied from memory
  // Uses generic markersConfig from store
  // ============================================================
  const fetchAndApplyMarkers = useCallback(async (symbol, tf, myLoadId) => {
    try {
      const cfg = useStore.getState().markersConfig;
      
      if (!cfg || !cfg.enabled) {
        layeredActiveRef.current = false;
        markersAllRef.current = [];
        applyMarkersFromCache();
        return;
      }

      // ── Layered mode (EMA strategy report) ──
      if (cfg.layers) {
        layeredActiveRef.current = true;
        layersRef.current = {
          trades: cfg.layers.trades || [],
          ema5:   cfg.layers.ema5   || [],
          price:  cfg.layers.price  || [],
        };
        markersAllRef.current = [];
        applyLayeredMarkersRef.current?.();
        return;
      }
      layeredActiveRef.current = false;

      // ── Case 1: report passed actual data — use directly, no DB fetch ──
      // ── Case 1: report passed actual data — use directly, no DB fetch ──
      if (cfg.markersBySymbol) {
        
        // Guard — symbol may have changed while we were processing
        if (symbol !== currentSymbolRef.current) return;
        const data = cfg.markersBySymbol[symbol] || [];

        if (cfg.markerKind === 'trade') {
          // Trades review: build entry/exit markers snapped to candles.
          // Uses the current OHLCV cache; re-applied on scroll via
          // applyMarkersFromCache (markersAllRef persists the set).
          const cache      = ohlcvCacheRef.current;
          const cacheTimes = cache.map(c => c.time);
          markersAllRef.current = buildTradeMarkersFromRows(data, cacheTimes, cache);
        } else {
          markersAllRef.current = buildVPatternMarkers(data);
        }

        applyMarkersFromCache();
        return;
      }

      // ── Case 2: fetchFn provided — fetch from DB ──
      if (!cfg.fetchFn) {
        markersAllRef.current = [];
        applyMarkersFromCache();
        return;
      }

      const fetchFn = typeof cfg.fetchFn === 'string'
        ? MARKER_FETCH_FNS[cfg.fetchFn]
        : cfg.fetchFn;

      if (!fetchFn) return;

      const filters   = cfg.filters || {};
      const markersTf = filters.timeframe || tf;
      const data      = await fetchFn(symbol, markersTf, filters);

      // Guard — symbol may have changed during async fetch
      if (symbol !== currentSymbolRef.current) return;
      if (myLoadId !== undefined && myLoadId !== loadIdRef.current) return;

      markersAllRef.current = buildVPatternMarkers(data || []);
      applyMarkersFromCache();
    } catch (_) {}
  }, []);

  // ============================================================
  // APPLY LAYERED MARKERS — trades + EMA5/price pivots, anchored
  // to the EMA5 series so they render even when price is hidden
  // ============================================================
  const applyLayeredMarkers = useCallback(() => {
    if (!chartRef.current) return;
    // Markers must anchor to the main price series — the LWC marker
    // plugin does not reliably paint on secondary line series (EMA5).
    const anchor = mainSeriesRef.current || candleSeriesRef.current;
    if (!anchor) return;

    const showM = useStore.getState().showMarkers;
    const t     = togglesRef.current;
    const cache = ohlcvCacheRef.current;

    let combined = [];
    if (showM) {
      if (t.showTrades)   combined = combined.concat(buildLayeredMarkers('trades', layersRef.current.trades));
      if (t.showEma5Piv)  combined = combined.concat(buildLayeredMarkers('ema5',   layersRef.current.ema5));
      if (t.showPricePiv) combined = combined.concat(buildLayeredMarkers('price',  layersRef.current.price));
    }
    combined.sort((a, b) => a.time - b.time);

    const filtered = cache.length > 0
      ? combined.filter(m => m.time >= cache[0].time && m.time <= cache[cache.length - 1].time)
      : combined;

    if (filtered.length > 0) applyMarkersToSeries(anchor, filtered);
    else removeMarkersFromSeries(anchor);
    
  }, []);
  
  const applyLayeredMarkersRef = useRef(null);
  applyLayeredMarkersRef.current = applyLayeredMarkers;

  
  // Build trade markers, snapped to the candle bucket that CONTAINS
  // each trade's timestamp — works across all 6 timeframes.
  const buildTradeMarkers = useCallback(() => {
    const trades = tradesRawRef.current;
    const cache  = ohlcvCacheRef.current;
    if (!trades.length || !cache.length) return [];

    const times = cache.map(c => c.time);   // ascending
    // index of the largest bucket time <= t (the bucket containing t)
    const snap = (t) => {
      let lo = 0, hi = times.length - 1, ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (times[mid] <= t) { ans = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      return ans;
    };

    const out = [];
    
    trades.forEach((tr) => {
      // reuse the chart's own bucket→time conversion for consistency
      const eT = toChartTime(`${String(tr.entry_date).slice(0,10)}T${tr.entry_time || '09:15:00'}`);
      const ei = snap(eT);
      
      if (ei >= 0) {
        const isBuy = (tr.entry_action || '').toLowerCase().trim() === 'buy';
        out.push({ time: cache[ei].time, position: 'belowBar', shape: 'arrowUp',
                   color: isBuy ? '#26a69a' : '#ef5350', text: isBuy ? 'B' : 'S' });
      }
      if (tr.exit_date) {
        const xT = toChartTime(`${String(tr.exit_date).slice(0,10)}T${tr.exit_time || '15:30:00'}`);
        const xi = snap(xT);
        if (xi >= 0) {
          const isBuy = (tr.exit_action || '').toLowerCase().trim() === 'buy';
          out.push({ time: cache[xi].time, position: 'aboveBar', shape: 'arrowDown',
                     color: isBuy ? '#26a69a' : '#ef5350', text: 'X' });
        }
      }
    });
    return out;
  }, []);
  
  // ============================================================
  // APPLY MARKERS FROM CACHE — filter to visible range
  // Called on: scroll, markers toggle, markers fetch complete
  // ============================================================
  const applyMarkersFromCache = useCallback(() => {
    if (!chartRef.current) return;
    
    if (layeredActiveRef.current) { applyLayeredMarkersRef.current?.(); return; }

    const mainSeries = mainSeriesRef.current || candleSeriesRef.current;
    if (!mainSeries) return;

    const showM  = useStore.getState().showMarkers;
    const cache  = ohlcvCacheRef.current;

    const reviewMode = useStore.getState().tradeReviewMode;
    const hasTrade   = reviewMode && tradesRawRef.current.length > 0;
    if (!showM || (markersAllRef.current.length === 0 && !hasTrade)) {
      removeMarkersFromSeries(mainSeries);
      return;
    }

    // Report markers + trade markers (trade markers only populated in review mode)
    const allMarkers = [...markersAllRef.current, ...buildTradeMarkers()]
      .sort((a, b) => a.time - b.time);

    // Filter to visible candle range
    const filtered = cache.length > 0
      ? allMarkers.filter(m => m.time >= cache[0].time && m.time <= cache[cache.length - 1].time)
      : allMarkers;
    

    if (filtered.length > 0) {
      applyMarkersToSeries(mainSeries, filtered);
    } else {
      removeMarkersFromSeries(mainSeries);
    }
  }, []);

  const fetchTradeMarkers = useCallback(async () => {
    const reviewMode = useStore.getState().tradeReviewMode;
    const symbol     = useStore.getState().selectedSymbol;

    // If the new selection-scoped trade markers are active
    // (markerKind:'trade' from Load-on-Chart), the DB-fetch path is
    // disabled — markersAllRef owns the markers. Prevents double markers.
    const cfg = useStore.getState().markersConfig;
    if (cfg && cfg.enabled && cfg.markerKind === 'trade') {
      tradesRawRef.current = [];
      applyMarkersFromCache();
      return;
    }

    if (!reviewMode || !symbol) {
      tradesRawRef.current = [];
      applyMarkersFromCache();
      return;
    }
    try {
      const data = await fetchTradesForChart(symbol);
      if (symbol !== useStore.getState().selectedSymbol) return;  // stale guard
      tradesRawRef.current = data || [];
    } catch (_) {
      tradesRawRef.current = [];
    }
    applyMarkersFromCache();
  }, [applyMarkersFromCache]);

  useEffect(() => {
    fetchTradeMarkers();
  }, [selectedSymbol, tradeReviewMode]);
  
  
    // Rebuild trade markers (Load-on-Chart path) against the CURRENT cache.
  // Trade markers are snapped to candles, so when the cache grows via
  // lazy-load, the marker set must be recomputed — not just re-filtered.
  const rebuildTradeMarkersIfActive = useCallback(() => {
    const cfg = useStore.getState().markersConfig;
    if (!cfg || !cfg.enabled || cfg.markerKind !== 'trade') return;

    const symbol = useStore.getState().selectedSymbol;
    const rows   = (cfg.markersBySymbol && cfg.markersBySymbol[symbol]) || [];
    const cache  = ohlcvCacheRef.current;
    if (!cache.length) return;

    const cacheTimes = cache.map(c => c.time);
    markersAllRef.current = buildTradeMarkersFromRows(rows, cacheTimes, cache);
  }, []);
    
  
  // ============================================================
  // LAZY LOAD LEFT — older candles
  // Prepends to cache, preserves viewport
  // ============================================================
  const loadMoreLeft = useCallback(async () => {
    if (!chartRef.current) return;
    if (isLoadingMoreRef.current) return;
    if (!hasMoreLeftRef.current) return;
    if (!oldestBucketRef.current) return;

    const symbol = useStore.getState().selectedSymbol;
    const tf     = useStore.getState().chartSettings[gridIndex].timeframe;
    const src    = useStore.getState().chartSettings[gridIndex].emaSource;
    isLoadingMoreRef.current = true;

    try {
      const toTime   = new Date(new Date(oldestBucketRef.current).getTime() - 1000).toISOString();
      const candles  = await fetchCandles(symbol, tf, INITIAL_LIMIT, toTime);

      if (!chartRef.current) return;
      if (!candles || candles.length === 0) {
        hasMoreLeftRef.current = false;
        return;
      }
      if (candles.length < INITIAL_LIMIT) hasMoreLeftRef.current = false;

      // Save viewport before prepend
      const ts        = chartRef.current.timeScale();
      const prevRange = ts.getVisibleLogicalRange();

      const newOhlcv = candles.map(c => ({
        time:  toChartTime(c.bucket),
        open:  parseFloat(c.open),
        high:  parseFloat(c.high),
        low:   parseFloat(c.low),
        close: parseFloat(c.close),
      }));

      const merged = [...newOhlcv, ...ohlcvCacheRef.current];
      ohlcvCacheRef.current   = merged;
      oldestBucketRef.current = candles[0].bucket;

      // Update all price series
      candleSeriesRef.current?.setData(merged);
      ['close', 'high', 'low'].forEach(s => {
        lineSeriesRef.current[s]?.setData(
          merged.map(c => ({ time: c.time, value: c[s] }))
        );
      });

      // Restore viewport — shift by prepended count
      if (prevRange) {
        const shift = newOhlcv.length;
        ts.setVisibleLogicalRange({
          from: prevRange.from + shift,
          to:   prevRange.to  + shift,
        });
      }

      // Fetch indicators for new range
      const indicators = await fetchIndicatorsByRange(
        symbol, tf,
        candles[0].bucket,
        candles[candles.length - 1].bucket,
      );
      if (indicators && indicators.length > 0) {
        const merged2 = [...indicators, ...indicatorCacheRef.current];
        indicatorCacheRef.current = merged2;
        applyIndicatorDataToEMAs(merged2, src);
        applyEMAVisibility();
      }

      rebuildTradeMarkersIfActive();
      applyMarkersFromCache();
      // ── TREND LINE — recompute after left scroll loads more candles ──
      const tlSet = useStore.getState().chartSettings[gridIndex];
      drawTrendLine(trendLineSeriesRef, ohlcvCacheRef.current,
                    tlSet.showTrendLine, 3, tlSet.showTrendLineMarkers);
      // ── TREND LINE ENGINE (new) ──
      drawTrendlineEngine(trendlineEngineSeriesRef, ohlcvCacheRef.current,
                          tlSet.showTrendlineEngine, tlSet.trendlineN ?? 3,
                          trendlineEngineStatesRef);
    } catch (_) {
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [gridIndex, applyChartTypeVisibility, applyIndicatorDataToEMAs, applyEMAVisibility, applyMarkersFromCache]);

  // ============================================================
  // LAZY LOAD RIGHT — newer candles
  // ============================================================
  const loadMoreRight = useCallback(async () => {
    if (!chartRef.current) return;
    if (isLoadingMoreRightRef.current) return;
    if (!hasMoreRightRef.current) return;
    if (!newestBucketRef.current) return;

    const symbol = useStore.getState().selectedSymbol;
    const tf     = useStore.getState().chartSettings[gridIndex].timeframe;
    const src    = useStore.getState().chartSettings[gridIndex].emaSource;
    isLoadingMoreRightRef.current = true;

    try {
      const candles = await fetchCandlesAfter(symbol, tf, newestBucketRef.current, INITIAL_LIMIT);
      if (!chartRef.current) return;
      if (!candles || candles.length === 0) {
        hasMoreRightRef.current = false;
        return;
      }
      if (candles.length < INITIAL_LIMIT) hasMoreRightRef.current = false;

      const newOhlcv = candles.map(c => ({
        time:  toChartTime(c.bucket),
        open:  parseFloat(c.open),
        high:  parseFloat(c.high),
        low:   parseFloat(c.low),
        close: parseFloat(c.close),
      }));

      // Save viewport before append — right-append doesn't shift indices,
      // so restoring the same logical range keeps the user in place.
      const tsR        = chartRef.current.timeScale();
      const prevRangeR = tsR.getVisibleLogicalRange();

      const merged = [...ohlcvCacheRef.current, ...newOhlcv];
      ohlcvCacheRef.current   = merged;
      newestBucketRef.current = candles[candles.length - 1].bucket;

      candleSeriesRef.current?.setData(merged);
      ['close', 'high', 'low'].forEach(s => {
        lineSeriesRef.current[s]?.setData(
          merged.map(c => ({ time: c.time, value: c[s] }))
        );
      });

      // Restore viewport — unchanged indices, so no jump
      if (prevRangeR) {
        tsR.setVisibleLogicalRange({ from: prevRangeR.from, to: prevRangeR.to });
      }

      // Fetch indicators
      const indicators = await fetchIndicatorsByRange(
        symbol, tf,
        candles[0].bucket,
        candles[candles.length - 1].bucket,
      );
      if (indicators && indicators.length > 0) {
        const merged2 = [...indicatorCacheRef.current, ...indicators];
        indicatorCacheRef.current = merged2;
        applyIndicatorDataToEMAs(merged2, src);
        applyEMAVisibility();
      }

      rebuildTradeMarkersIfActive();
      applyMarkersFromCache();
    } catch (_) {
    } finally {
      isLoadingMoreRightRef.current = false;
    }
  }, [gridIndex, applyIndicatorDataToEMAs, applyEMAVisibility, applyMarkersFromCache]);

  // ============================================================
  // JUMP TO DATE
  // ============================================================
  const jumpToDate = async (dateStr) => {
  if (!chartRef.current || !dateStr) return;
  isInitialLoadRef.current = true;   // block scroll handler during jump
  activeJumpDateRef.current = dateStr;

    const myLoadId = ++loadIdRef.current;
    const symbol   = useStore.getState().selectedSymbol;
    const tf       = useStore.getState().chartSettings[gridIndex].timeframe;
    const src      = useStore.getState().chartSettings[gridIndex].emaSource;
    currentSymbolRef.current = symbol;   // ← keep marker-rebuild guard in sync
                                         //   (loadChartData sets this too; jumpToDate must as well)

    setLoading(true);
    setError(null);

    oldestBucketRef.current = null;
    newestBucketRef.current = null;
    isLoadingMoreRef.current      = false;
    isLoadingMoreRightRef.current = false;
    hasMoreLeftRef.current  = true;
    hasMoreRightRef.current = true;

    try {
      const toTime   = `${dateStr}T23:59:59+05:30`;
      const fromTime = `${dateStr}T00:00:00+05:30`;

      const [before, after] = await Promise.all([
        fetchCandles(symbol, tf, INITIAL_LIMIT, toTime),
        fetchCandlesAfter(symbol, tf, fromTime, INITIAL_LIMIT),
      ]);
      if (myLoadId !== loadIdRef.current) {
        isInitialLoadRef.current = false;  // ← FIX: Re-enable scroll handler
        return;
      }

      const allRaw = [...(before || []), ...(after || [])];
      const seen   = new Set();
      const deduped = allRaw
        .filter(c => { if (seen.has(c.bucket)) return false; seen.add(c.bucket); return true; })
        .sort((a, b) => new Date(a.bucket) - new Date(b.bucket));

      if (deduped.length === 0) { setError('No data for this date'); setLoading(false); return; }

      const ohlcv = deduped.map(c => ({
        time:  toChartTime(c.bucket),
        open:  parseFloat(c.open),
        high:  parseFloat(c.high),
        low:   parseFloat(c.low),
        close: parseFloat(c.close),
      }));
      ohlcvCacheRef.current   = ohlcv;
      
      oldestBucketRef.current = deduped[0].bucket;
      newestBucketRef.current = deduped[deduped.length - 1].bucket;

      candleSeriesRef.current?.setData(ohlcv);
      ['close', 'high', 'low'].forEach(s => {
        lineSeriesRef.current[s]?.setData(ohlcv.map(c => ({ time: c.time, value: c[s] })));
      });
      applyChartTypeVisibility();

      // Find target date candles and center view
      const IST_START = Math.floor(
        new Date(`${dateStr}T00:00:00+05:30`).getTime() / 1000
      );
      // First candle ON the trade day (start of that day's session)
      let targetIdx = ohlcv.findIndex(c => c.time >= IST_START);
      if (targetIdx < 0) targetIdx = ohlcv.length - 1;

      // Candles each side of the target. ~120 total fits comfortably on a
      // single-panel screen; fewer on higher timeframes so a day/week isn't
      // a lost sliver, more on intraday. Timeframe-aware, resolution-independent.
      const HALF_BY_TF = {
        '2min': 70, '5min': 60, '15min': 55,
        '1H': 45, '1D': 35, '1W': 22,
      };
      const HALF = HALF_BY_TF[tf] || 60;
      const len  = ohlcv.length;

      // Center the target. Clamp to available data WITHOUT sliding the target
      // off-centre: if a side is short, just shrink that side. This keeps the
      // trade candle in the middle instead of drifting to an edge.
      let from = Math.max(0, targetIdx - HALF);
      let to   = Math.min(len - 1, targetIdx + HALF);

      // Safety: if the target somehow lands at the very edge (trade at start/
      // end of loaded data), nudge the window so it stays a few bars inside.
      if (targetIdx <= from + 2) from = Math.max(0, targetIdx - 3);
      if (targetIdx >= to - 2)   to   = Math.min(len - 1, targetIdx + 3);

      const indicators = await fetchIndicatorsByRange(
        symbol, tf,
        deduped[0].bucket, deduped[deduped.length - 1].bucket
      );
      if (myLoadId !== loadIdRef.current) { isInitialLoadRef.current = false; return; }
      if (indicators && indicators.length > 0) {
        indicatorCacheRef.current = indicators;
        applyIndicatorDataToEMAs(indicators, src);
        applyEMAVisibility();
      }

      await fetchAndApplyMarkers(symbol, tf, myLoadId);
      if (myLoadId !== loadIdRef.current) { isInitialLoadRef.current = false; return; }

      requestAnimationFrame(() => {
        if (myLoadId !== loadIdRef.current) {
          isInitialLoadRef.current = false;
          return;
        }
        chartRef.current?.timeScale().setVisibleLogicalRange({ from, to });
        // Re-enable the scroll handler shortly after the range is set, so an
        // early range event during settling doesn't trip loadMoreLeft.
        setTimeout(() => {
          if (myLoadId === loadIdRef.current) {
            isInitialLoadRef.current = false;
          }
        }, 100);
      });

      setLoading(false);
    } catch (_) {
      isInitialLoadRef.current = false;   // re-enable on error too
      setLoading(false);
    }
  };

  // ============================================================
  // ZOOM CONTROLS
  // ============================================================
  const zoomIn = () => {
    const ts    = chartRef.current?.timeScale();
    const range = ts?.getVisibleLogicalRange();
    if (!range) return;
    const center = (range.from + range.to) / 2;
    const half   = (range.to - range.from) / 4;
    ts.setVisibleLogicalRange({ from: center - half, to: center + half });
  };

  const zoomOut = () => {
    const ts    = chartRef.current?.timeScale();
    const range = ts?.getVisibleLogicalRange();
    if (!range) return;
    const center = (range.from + range.to) / 2;
    const half   = (range.to - range.from);
    ts.setVisibleLogicalRange({ from: center - half, to: center + half });
  };

  const zoomFit = () => {
    chartRef.current?.timeScale().fitContent();
  };

  const zoomReset = () => {
    const total = ohlcvCacheRef.current.length;
    if (total === 0) return;
    const tf       = useStore.getState().chartSettings[gridIndex].timeframe;
    const rightPad = rightPadFor(tf);
    const span     = defaultBars(tf) + rightPad;
    chartRef.current?.timeScale().setVisibleLogicalRange({
      from: total + rightPad - span,
      to:   total + rightPad,
    });
  };

  // ============================================================
  // RENDER
  // ============================================================
  const symbol   = selectedSymbol || '';
  const TIMEFRAMES = ['2min', '5min', '15min', '1H', '1D', '1W'];

  return (
    <div
      style={s.container}
      onMouseEnter={() => { activeChartIndex = gridIndex; }}
    >

      {/* ── Toolbar ── */}
      <div style={s.toolbar}>

        {/* Symbol */}
        <span style={s.symbolLabel}>{symbol}</span>

        {/* Timeframe buttons */}
        {TIMEFRAMES.map(tf => (
          <button key={tf}
            style={{ ...s.tfBtn, ...(timeframe === tf ? s.tfBtnActive : {}) }}
            onClick={() => setTimeframe(tf)}>
            {tf}
          </button>
        ))}

        {/* Chart type toggle */}
        <button
          style={{ ...s.iconBtn, ...(chartType === 'candlestick' ? s.iconBtnActive : {}) }}
          onClick={() => setChartType('candlestick')}
          title="Candlestick">
          ▐▌
        </button>
        <button
          style={{ ...s.iconBtn, ...(chartType === 'line' ? s.iconBtnActive : {}) }}
          onClick={() => setChartType('line')}
          title="Line">
          〜
        </button>

        {/* Price on/off — hide price to review EMAs only */}
        <button
          style={{ ...s.iconBtn, ...(!showPrice ? s.iconBtnActive : {}) }}
          onClick={() => setShowPrice(v => !v)}
          title={showPrice ? 'Hide price (EMAs only)' : 'Show price'}>
          {showPrice ? '👁 Price' : '🚫 Price'}
        </button>

        {/* Overlay layer toggles — only when opened from the EMA strategy report */}
        {layeredMode && (
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#555', marginLeft: '6px' }}>
              <input type="checkbox" checked={showTrades} onChange={e => setShowTrades(e.target.checked)} /> Trades
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#555' }}>
              <input type="checkbox" checked={showEma5Piv} onChange={e => setShowEma5Piv(e.target.checked)} /> EMA5 Piv
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#555' }}>
              <input type="checkbox" checked={showPricePiv} onChange={e => setShowPricePiv(e.target.checked)} /> Price Piv
            </label>
          </>
        )}

        {/* Line source toggles — only when line mode */}
        {chartType === 'line' && ['close', 'high', 'low'].map(src => (
          <button key={src}
            style={{ ...s.srcBtn, ...(lineSources.includes(src) ? s.srcBtnActive : {}) }}
            onClick={() => toggleLineSource(src)}>
            {src.charAt(0).toUpperCase() + src.slice(1)}
          </button>
        ))}

        {/* Indicators */}
        <button style={s.indBtn} onClick={() => setShowModal(true)}>
          Indicators
        </button>

        {/* Zoom controls */}
        <button style={s.zoomBtn} onClick={zoomIn}  title="Zoom in">+</button>
        <button style={s.zoomBtn} onClick={zoomOut} title="Zoom out">−</button>
        <button style={s.zoomBtn} onClick={zoomFit} title="Fit content">⊡</button>

        {/* Jump to date */}
        <input
          type="date"
          style={s.dateInput}
          value={jumpDate}
          onChange={e => setJumpDate(e.target.value)}
        />
        <button style={s.goBtn} onClick={() => jumpToDate(jumpDate)}>Go</button>

      </div>

      {/* ── Chart container ── */}
      <div style={s.chartWrap} ref={chartContainerRef}>

        {/* Legend — absolute overlay, no layout impact */}
        {legend && (
          <div style={s.legend}>
            {legend.ohlc && chartType === 'candlestick' && (
              <span style={s.legendOhlc}>
                O <b>{legend.ohlc.o?.toFixed(2)}</b>
                {' '}H <b>{legend.ohlc.h?.toFixed(2)}</b>
                {' '}L <b>{legend.ohlc.l?.toFixed(2)}</b>
                {' '}C <b style={{ color: legend.ohlc.c >= legend.ohlc.o ? '#26a69a' : '#ef5350' }}>
                  {legend.ohlc.c?.toFixed(2)}
                </b>
              </span>
            )}
            {chartType === 'line' && legend.lineVal && Object.entries(legend.lineVal).map(([src, val]) =>
              lineSources.includes(src) ? (
                <span key={src} style={s.legendItem}>
                  {src.charAt(0).toUpperCase() + src.slice(1)}: <b>{val?.toFixed(2)}</b>
                </span>
              ) : null
            )}
            {Object.entries(legend.emas).filter(([ema]) => visibleEMAs[ema]).map(([ema, val]) => {
              const color = settings.emaStyles[ema]?.color || '#888';
              return (
                <span key={ema} style={{ ...s.legendItem, color }}>
                  {ema.replace('ema_', 'EMA')}: <b>{val?.toFixed(2)}</b>
                </span>
              );
            })}
            {legend.trend && (
              <span style={{ ...s.legendItem, color: '#2962FF' }}>
                Trend: <b>{legend.trend.state}</b>
                {legend.trend.sl != null && <> · SL <b>{Number(legend.trend.sl).toFixed(2)}</b></>}
                {' '}· <b>{legend.trend.action}</b>
              </span>
            )}
          </div>
        )}

        {loading && (
          <div style={s.loadingOverlay}>
            <span style={s.loadingText}>Loading...</span>
          </div>
        )}
        {error && (
          <div style={s.errorOverlay}>
            <span style={s.errorText}>{error}</span>
          </div>
        )}
      </div>

      {/* ── Indicators Modal ── */}
      {showModal && (
        <IndicatorsModal
          settings={settings}
          onToggleEMA={toggleEMA}
          onSetEMASource={setEMASource}
          onSetEMAStyle={setEMAStyle}
          onSetLineStyle={setLineStyle}
          onClose={() => setShowModal(false)}
          isGrid={isGrid}
          gridIndex={gridIndex}
          toggleTrendLine={toggleTrendLine}
          toggleTrendLineMarkers={toggleTrendLineMarkers}
          toggleTrendlineEngine={toggleTrendlineEngine}
          onSetTrendlineN={(v) => setChartSetting(gridIndex, 'trendlineN', v)}
        />
      )}
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const s = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    overflow:      'hidden',
    background:    '#fff',
    position:      'relative',
  },
  toolbar: {
    display:         'flex',
    alignItems:      'center',
    gap:             '3px',
    padding:         '4px 8px',
    borderBottom:    '1px solid #e0e0e0',
    background:      '#fff',
    flexShrink:      0,
    flexWrap:        'wrap',
  },
  symbolLabel: {
    fontWeight:  'bold',
    fontSize:    '13px',
    color:       '#131722',
    marginRight: '6px',
    minWidth:    '50px',
  },
  tfBtn: {
    padding:      '3px 8px',
    border:       '1px solid #e0e0e0',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '11px',
    color:        '#555',
  },
  tfBtnActive: {
    background:  '#2962ff',
    color:       '#fff',
    borderColor: '#2962ff',
  },
  iconBtn: {
    padding:      '3px 8px',
    border:       '1px solid #e0e0e0',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '13px',
    color:        '#555',
  },
  iconBtnActive: {
    background:  '#2962ff',
    color:       '#fff',
    borderColor: '#2962ff',
  },
  srcBtn: {
    padding:      '3px 7px',
    border:       '1px solid #e0e0e0',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '11px',
    color:        '#555',
  },
  srcBtnActive: {
    background:  '#1565c0',
    color:       '#fff',
    borderColor: '#1565c0',
  },
  indBtn: {
    padding:      '3px 10px',
    border:       '1px solid #e0e0e0',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '11px',
    color:        '#444',
  },
  zoomBtn: {
    padding:      '3px 7px',
    border:       '1px solid #e0e0e0',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '13px',
    color:        '#555',
  },
  dateInput: {
    padding:      '3px 6px',
    border:       '1px solid #e0e0e0',
    borderRadius: '3px',
    fontSize:     '11px',
    color:        '#444',
  },
  goBtn: {
    padding:      '3px 8px',
    border:       '1px solid #2962ff',
    borderRadius: '3px',
    background:   '#2962ff',
    color:        '#fff',
    cursor:       'pointer',
    fontSize:     '11px',
  },
  chartWrap: {
    flex:     1,
    position: 'relative',
    overflow: 'hidden',
  },
  loadingOverlay: {
    position:       'absolute',
    top:            0, left: 0, right: 0, bottom: 0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     'rgba(255,255,255,0.7)',
    zIndex:         10,
  },
  loadingText: {
    fontSize: '13px',
    color:    '#888',
  },
  errorOverlay: {
    position:       'absolute',
    top:            0, left: 0, right: 0, bottom: 0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         10,
  },
  errorText: {
    fontSize: '13px',
    color:    '#ef5350',
  },
  legend: {
    position:        'absolute',
    top:             8,
    left:            8,
    zIndex:          10,
    display:         'flex',
    alignItems:      'center',
    gap:             '8px',
    padding:         '3px 8px',
    fontSize:        '11px',
    color:           '#333',
    flexWrap:        'wrap',
    pointerEvents:   'none',
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderRadius:    '3px',
  },
  legendOhlc: { color: '#333' },
  legendItem: { color: '#555' },
};

// ── Modal styles ──
const modal = {
  overlay: {
    position:       'fixed',
    top:            0, left: 0, right: 0, bottom: 0,
    background:     'rgba(0,0,0,0.3)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         1000,
  },
  box: {
    background:   '#fff',
    borderRadius: '8px',
    padding:      '20px',
    minWidth:     '320px',
    maxWidth:     '480px',
    boxShadow:    '0 4px 20px rgba(0,0,0,0.15)',
    maxHeight:    '80vh',
    overflowY:    'auto',
  },
  header: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    marginBottom:   '16px',
  },
  title: {
    fontWeight: 'bold',
    fontSize:   '15px',
    color:      '#131722',
  },
  closeBtn: {
    background: 'none',
    border:     'none',
    fontSize:   '16px',
    cursor:     'pointer',
    color:      '#666',
  },
  section: {
    marginBottom: '16px',
  },
  sectionLabel: {
    fontSize:     '11px',
    fontWeight:   'bold',
    color:        '#999',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  styleRow: {
    display:      'flex',
    alignItems:   'center',
    gap:          '8px',
    marginBottom: '6px',
  },
  checkRowInline: {
    display:    'flex',
    alignItems: 'center',
    cursor:     'pointer',
    minWidth:   '90px',
  },
  colorDot: {
    width:        '10px',
    height:       '10px',
    borderRadius: '50%',
    display:      'inline-block',
    marginRight:  '4px',
  },
  checkLabel: {
    fontSize: '12px',
    color:    '#333',
  },
  colorPicker: {
    width:  '28px',
    height: '24px',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  widthBtns: {
    display: 'flex',
    gap:     '3px',
  },
  widthBtn: {
    width:        '24px',
    height:       '22px',
    border:       '1px solid #ddd',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '11px',
    color:        '#555',
  },
  widthBtnActive: {
    background:  '#2962ff',
    color:       '#fff',
    borderColor: '#2962ff',
  },
  radioRow: {
    display: 'flex',
    gap:     '12px',
  },
  radioLabel: {
    display:    'flex',
    alignItems: 'center',
    fontSize:   '12px',
    color:      '#444',
    cursor:     'pointer',
  },
};

export default ChartNew;